import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HandCoins, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader, PageHeader } from "@/components/common";
import { Confirm } from "@/components/kids/kidsUi";
import { useLiveRefresh } from "@/lib/live";
import SplitForm from "@/components/splits/SplitForm";
import SplitCard from "@/components/splits/SplitCard";
import { writesLocked } from "@/lib/splits";

const GREEN = "var(--glow-money)";

function Balances({ hub, d, onChanged }) {
  const [settle, setSettle] = useState(null);
  const go = () => api.post(`/organizations/${hub.id}/splits/settle`, { other_key: settle.other_key }).then(() => { toast.success("Settled up"); setSettle(null); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <section className="q-card mb-6 p-4" data-testid="who-owes-what">
      <h2 className="mb-2 flex items-center gap-2 font-display text-lg font-bold text-q-text"><HandCoins className="h-4 w-4" style={{ color: `rgb(${GREEN})` }} /> Who owes what</h2>
      {!d.balances.length ? <p className="text-sm text-q-muted" data-testid="balances-settled">All settled. Nobody owes anything.</p> : (
        <ul className="divide-y divide-q-line">
          {d.balances.map((b) => (
            <li key={`${b.debtor_key}-${b.creditor_key}`} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`balance-${b.debtor_key}-${b.creditor_key}`}>
              <span className="font-semibold text-q-text" data-testid={`balance-label-${b.debtor_key}-${b.creditor_key}`}>{b.label}</span>
              {b.can_settle && <button className="q-btn q-btn-secondary min-h-[44px]" disabled={writesLocked(hub)} onClick={() => setSettle(b)} data-testid={`settle-btn-${b.other_key}`}>Settle up</button>}
            </li>
          ))}
        </ul>
      )}
      <Confirm open={!!settle} onClose={() => setSettle(null)} title="Settle up?" body={settle ? `Mark ${money(settle.amount)} as settled between you and ${settle.other_name}?` : ""} confirmLabel="Mark settled" onConfirm={go} testid="settle-confirm" />
    </section>
  );
}

export default function Splits() {
  const { hub } = useHub();
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = useCallback(() => api.get(`/organizations/${hub.id}/splits`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "split", 30000);
  useEffect(() => {
    if (params.get("new")) { setForm({ prefill: { title: params.get("title") || "", amount: params.get("amount") || "", date: params.get("date") || undefined } }); setParams({}, { replace: true }); }
  }, [params, setParams]);
  if (!d) return <Loader />;
  const done = () => { setForm(null); load(); };
  return (
    <div data-testid="splits-page">
      <PageHeader eyebrow="Money" title="Splits" rgb={GREEN} testid="splits-title"
        actions={<button className="q-btn q-btn-primary min-h-[44px]" disabled={writesLocked(hub)} onClick={() => setForm({})} data-testid="split-new-btn"><Plus className="h-4 w-4" /> New split</button>} />
      <p className="mb-5 text-sm text-q-muted" data-testid="splits-note">{d.note}</p>
      <Balances hub={hub} d={d} onChanged={load} />
      {!d.splits.length ? <EmptyState title="No splits yet" hint="Split a shared cost with other adults in the family." rgb={GREEN} testid="splits-empty" /> : (
        <ul className="space-y-3" data-testid="splits-list">
          {d.splits.map((s) => <SplitCard key={s.id} hubId={hub.id} s={s} me={d.me} ro={writesLocked(hub)} onEdit={() => setForm({ split: s })} onChanged={load} />)}
        </ul>
      )}
      {form && <SplitForm hubId={hub.id} d={d} split={form.split} prefill={form.prefill} onClose={() => setForm(null)} onSaved={done} />}
    </div>
  );
}
